import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Newspaper, Award, Briefcase, Building2,
  Phone, Image as ImageIcon, Settings, LogOut, Menu, X, Leaf, ChevronDown,
  MapPin, Users, ImagePlus, Ribbon, BarChart3, Sparkles, Globe, Type, Target, MailOpen,
} from 'lucide-react';
import { useAdminAuth } from './AdminAuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ADMIN_T } from './labels';

const NAV = [
  { to: '/admin', key: 'dashboard', Icon: LayoutDashboard, end: true },
  { to: '/admin/news', key: 'news', Icon: Newspaper },
  { to: '/admin/certificates', key: 'certificates', Icon: Award },
  { to: '/admin/services', key: 'services', Icon: Briefcase },
  { to: '/admin/projects', key: 'projects', Icon: MapPin },
  { to: '/admin/partners', key: 'partners', Icon: Users },
  { to: '/admin/gallery', key: 'galleryPage', Icon: ImagePlus },
  { to: '/admin/client-logos', key: 'clientLogos', Icon: Ribbon },
  { to: '/admin/lists', key: 'lists', Icon: BarChart3 },
  { to: '/admin/heroes', key: 'heroes', Icon: Sparkles },
  { to: '/admin/global', key: 'global', Icon: Globe },
  { to: '/admin/section-copy', key: 'sectionCopy', Icon: Type },
  { to: '/admin/strategic-goals', key: 'strategicGoals', Icon: Target },
  { to: '/admin/contact-page', key: 'contactPage', Icon: MailOpen },
  { to: '/admin/company', key: 'company', Icon: Building2 },
  { to: '/admin/contact', key: 'contact', Icon: Phone },
  { to: '/admin/media', key: 'media', Icon: ImageIcon },
  { to: '/admin/settings', key: 'settings', Icon: Settings },
];

const AdminLayout = () => {
  const { status, user, logout } = useAdminAuth();
  const { language, toggleLanguage } = useLanguage();
  const isAr = language === 'ar';
  const t = ADMIN_T[language] || ADMIN_T.en;
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950">
        <div className="w-10 h-10 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin" />
      </div>
    );
  }

  if (status === 'guest') return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    nav('/admin/login', { replace: true });
  };

  return (
    <div
      dir={isAr ? 'rtl' : 'ltr'}
      className="min-h-screen flex bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100"
    >
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ===== SIDEBAR ===== */}
      <aside
        data-testid="admin-sidebar"
        className={`fixed lg:sticky top-0 z-40 lg:z-auto h-screen w-72 flex-shrink-0
                    bg-white dark:bg-slate-900 border-${isAr ? 'l' : 'r'} border-slate-200 dark:border-slate-800
                    transition-transform duration-300 ${mobileOpen ? 'translate-x-0' : (isAr ? 'translate-x-full' : '-translate-x-full')}
                    lg:translate-x-0 flex flex-col`}
      >
        {/* Brand */}
        <div className={`px-5 py-5 flex items-center gap-3 border-b border-slate-200 dark:border-slate-800 ${isAr ? 'flex-row-reverse' : ''}`}>
          <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue to-brand-green flex items-center justify-center shadow">
            <Leaf className="w-5 h-5 text-white" />
          </span>
          <div className={isAr ? 'text-right' : 'text-left'}>
            <p className="text-sm font-bold text-slate-900 dark:text-white leading-tight">{t.brand}</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">CMS</p>
          </div>
          <button
            type="button"
            className={`lg:hidden ${isAr ? 'mr-auto' : 'ml-auto'} w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800`}
            onClick={() => setMobileOpen(false)}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-1">
          {NAV.map(({ to, key, Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${key}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition
                 ${isActive
                   ? 'bg-gradient-to-r from-brand-blue/15 to-brand-green/10 text-brand-blue-700 dark:text-cyan-200 border border-brand-blue/20'
                   : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}
                 ${isAr ? 'flex-row-reverse text-right' : ''}`
              }
            >
              <Icon className="w-4.5 h-4.5 flex-shrink-0" style={{ width: 18, height: 18 }} />
              <span className="flex-1">{t[key]}</span>
            </NavLink>
          ))}
        </nav>

        {/* Sign out */}
        <div className="p-3 border-t border-slate-200 dark:border-slate-800">
          <button
            type="button"
            onClick={handleLogout}
            data-testid="admin-signout"
            className={`w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-xl
                       text-sm font-semibold text-slate-700 dark:text-slate-200
                       bg-slate-100 dark:bg-slate-800 hover:bg-red-50 dark:hover:bg-red-500/10
                       hover:text-red-700 dark:hover:text-red-300 transition
                       ${isAr ? 'flex-row-reverse' : ''}`}
          >
            <LogOut className="w-4 h-4" />
            <span>{t.signOut}</span>
          </button>
        </div>
      </aside>

      {/* ===== MAIN ===== */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white/90 dark:bg-slate-900/90 backdrop-blur border-b border-slate-200 dark:border-slate-800">
          <div className={`flex items-center gap-3 px-4 md:px-6 py-3 ${isAr ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              data-testid="admin-mobile-menu"
              className="lg:hidden w-9 h-9 rounded-lg flex items-center justify-center bg-slate-100 dark:bg-slate-800"
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={toggleLanguage}
              data-testid="admin-lang-toggle"
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                         bg-slate-100 dark:bg-slate-800 text-xs font-semibold
                         text-slate-700 dark:text-slate-200 hover:shadow transition"
            >
              {t.languageSwitch}
            </button>

            {/* User menu */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setUserMenuOpen((o) => !o)}
                data-testid="admin-user-menu"
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full
                           bg-slate-100 dark:bg-slate-800 text-xs font-semibold
                           text-slate-700 dark:text-slate-200 ${isAr ? 'flex-row-reverse' : ''}`}
              >
                <span className="w-6 h-6 rounded-full bg-gradient-to-br from-brand-blue to-brand-green text-white flex items-center justify-center text-[10px] font-bold">
                  {(user?.email || '?').charAt(0).toUpperCase()}
                </span>
                <span className="hidden sm:inline truncate max-w-[140px]">{user?.email}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              {userMenuOpen && (
                <div
                  className={`absolute mt-2 ${isAr ? 'left-0' : 'right-0'} w-56 rounded-xl bg-white dark:bg-slate-900
                              border border-slate-200 dark:border-slate-800 shadow-xl py-1 z-40`}
                  onMouseLeave={() => setUserMenuOpen(false)}
                >
                  <NavLink
                    to="/admin/settings"
                    onClick={() => setUserMenuOpen(false)}
                    data-testid="admin-go-settings"
                    className={`block px-4 py-2 text-sm hover:bg-slate-50 dark:hover:bg-slate-800 ${isAr ? 'text-right' : 'text-left'}`}
                  >
                    {t.changePasswordTitle}
                  </NavLink>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className={`w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 ${isAr ? 'text-right' : 'text-left'}`}
                  >
                    {t.signOut}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
